import React from 'react';
import { Panel, PanelType } from '@fluentui/react';
import { IStorefrontSettings } from '../../../models/Storefront';
import {
    AUTOCOMPLETE_PART_LABELS, AUTOCOMPLETE_PART_TOOLTIPS, AUTOCOMPLETE_STYLE_PARTS,
    AutocompleteStylePart, IAutocompleteConfig, SearchBarContext,
    readAutocompleteConfig, writeAutocompleteConfig,
} from './autocompleteConfig';
import { ISearchElementStyle } from './searchPageConfig';
import { ElementPropertyRows, StylePartAccordions } from './styleControls';

const CONTEXT_PANEL_TITLES: Record<SearchBarContext, string> = {
    header: 'Header Suggestions',
    homePage: 'Home Page Suggestions',
    searchPage: 'Search Page Suggestions',
};

interface IStorefrontAutocompleteStylePanelProps {
    context: SearchBarContext;
    isOpen: boolean;
    onDismiss: () => void;
    storefrontSettings: IStorefrontSettings;
    onStorefrontSettingsChange: (newSettings: IStorefrontSettings) => void;
    /** Seed which style part opens first, so click-to-edit lands on the element that was clicked. */
    initialOpenPart?: string;
}

/**
 * Styling for one search bar's suggestion panel.
 *
 * Its own Panel rather than rows inside the search bar's callout: seven style parts, each with the full
 * element control set, is far too much for a popover anchored to an input — it covered the very preview
 * the merchant is trying to style. Each search bar gets its own instance, because the header, home page
 * and search page popouts are styled independently.
 */
const StorefrontAutocompleteStylePanel: React.FC<IStorefrontAutocompleteStylePanelProps> = (props) => {
    const autocomplete: IAutocompleteConfig = readAutocompleteConfig(props.storefrontSettings, props.context);

    const updatePart = (part: AutocompleteStylePart, patch: Partial<ISearchElementStyle>) => {
        // Read from the live settings rather than the value captured above, so rapid edits across
        // several parts cannot clobber one another.
        const current = readAutocompleteConfig(props.storefrontSettings, props.context);
        props.onStorefrontSettingsChange(writeAutocompleteConfig(props.storefrontSettings, props.context, {
            ...current,
            [part]: { ...current[part], ...patch },
        }));
    };

    return (
        <Panel
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            headerText={CONTEXT_PANEL_TITLES[props.context]}
            type={PanelType.smallFixedFar}
            isBlocking={false}
            closeButtonAriaLabel='Close'
        >
            <p style={{ margin: '0 0 0.5em', fontSize: 12, color: '#605e5c' }}>
                Styling for the suggestion panel that drops below this search bar. Columns the bar does
                not show are configured on the search bar itself.
            </p>
            <StylePartAccordions
                key={props.initialOpenPart}
                initialOpenKey={props.initialOpenPart}
                parts={AUTOCOMPLETE_STYLE_PARTS.map((part) => ({
                    key: part,
                    label: AUTOCOMPLETE_PART_LABELS[part],
                    tooltip: AUTOCOMPLETE_PART_TOOLTIPS[part],
                    content: (
                        <ElementPropertyRows settingKeyPrefix={`storefront.autocomplete.${part}`}
                            value={autocomplete[part]}
                            onChange={(patch) => updatePart(part, patch)}
                        />
                    ),
                }))}
            />
        </Panel>
    );
};

export default StorefrontAutocompleteStylePanel;
